import React, { useState, useEffect } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';
import Chip from '@mui/material/Chip';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import { DataGrid } from '@mui/x-data-grid';
import DeleteIcon from '@mui/icons-material/Delete';
import PixRoundedIcon from '@mui/icons-material/PixRounded';
import PlayerAvatar from '../../components/ui/PlayerAvatar';
import EmptyState from '../../components/ui/EmptyState';
import api from '../../api';
import { formatCurrency } from '../../utils/image';

// "AAAA-MM" de uma data no horário de Brasília
const mesDe = (data) => {
  const partes = new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Sao_Paulo', year: 'numeric', month: '2-digit' }).formatToParts(new Date(data));
  return `${partes.find((p) => p.type === 'year').value}-${partes.find((p) => p.type === 'month').value}`;
};

const SITUACAO_MENSALIDADE = {
  paga: { label: 'Paga', color: 'success' },
  aberta: { label: 'Em aberto', color: 'error' },
  saldo: { label: 'Paga com saldo', color: 'success' },
  sem_pix: { label: 'Sem cobrança PIX', color: 'default' },
  cancelled: { label: 'PIX cancelado', color: 'default' },
  refunded: { label: 'Estornada', color: 'warning' },
};

// Resumo do mês selecionado: peladas jogadas, cobranças e pontos para conferir
const AnaliseMes = ({ analise }) => {
  if (!analise) return <Typography variant="body2" color="text.secondary">Carregando o mês...</Typography>;
  if (analise.erro) return <MuiAlert severity="error">Não foi possível carregar a análise do mês.</MuiAlert>;
  const { resumo, peladas, mensalidades, alertas } = analise;
  const numero = (label, valor, cor) => (
    <Box sx={{ flex: '1 1 110px', p: 1.25, borderRadius: 2, bgcolor: 'background.default' }}>
      <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
      <Typography fontWeight={700} sx={{ color: cor }}>{valor}</Typography>
    </Box>
  );
  return (
    <Stack spacing={1.5}>
      <Stack direction="row" flexWrap="wrap" useFlexGap spacing={1}>
        {numero('Jogou', `${resumo.jogou} de ${resumo.peladasNoMes} peladas`)}
        {numero('Convidados', resumo.convidados)}
        {numero('Cobrado no mês', formatCurrency(resumo.cobrado), '#C62828')}
        {numero('Pago no mês', formatCurrency(resumo.pago), '#1F9D55')}
      </Stack>
      {peladas.length > 0 && (
        <Stack direction="row" flexWrap="wrap" useFlexGap spacing={0.75}>
          {peladas.map((p) => (
            <Chip
              key={p._id}
              size="small"
              label={`${p.dia}${p.convidados ? ` +${p.convidados}` : ''}`}
              color={p.jogou ? 'success' : 'default'}
              variant={p.jogou ? 'filled' : 'outlined'}
              title={p.jogou ? 'Jogou' : 'Não jogou'}
            />
          ))}
        </Stack>
      )}
      {mensalidades.length > 0 ? (
        mensalidades.map((m) => {
          const s = SITUACAO_MENSALIDADE[m.situacao] || { label: m.situacao, color: 'default' };
          return (
            <Stack key={m._id} direction="row" spacing={1} alignItems="center">
              <Typography variant="body2" sx={{ flexGrow: 1, minWidth: 0 }} noWrap>{m.descricao} · {formatCurrency(m.valor)}</Typography>
              <Chip size="small" label={s.label} color={s.color} />
            </Stack>
          );
        })
      ) : (
        <Typography variant="body2" color="text.secondary">Nenhuma mensalidade lançada neste mês.</Typography>
      )}
      {alertas.length === 0 ? (
        <MuiAlert severity="success">Cobranças do mês conferem com as peladas jogadas.</MuiAlert>
      ) : (
        alertas.map((a, i) => <MuiAlert key={i} severity={a.tipo}>{a.texto}</MuiAlert>)
      )}
    </Stack>
  );
};

const FinancialUserDetails = () => {
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [financialData, setFinancialData] = useState([]);
  const [balance, setBalance] = useState(0);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [registerSelect, setRegisterSelect] = useState(null);
  const [severity, setSeverity] = useState('success');
  const [openMessage, setOpenMessage] = useState(false);
  const [message, setMessage] = useState('');
  const [mes, setMes] = useState(mesDe(new Date()));
  const [soDoMes, setSoDoMes] = useState(true);
  const [analise, setAnalise] = useState(null);

  const notify = (text, type = 'success') => {
    setMessage(text);
    setSeverity(type);
    setOpenMessage(true);
  };

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await api.get('user/showAll');
        setUsers(response.data);
      } catch (error) {
        notify('Erro ao obter usuários', 'error');
      }
    };

    fetchUsers();
  }, []);

  const handleDeleteRegister = async () => {
    try {
      const response = await api.post('/removeRegister', { id: registerSelect });
      if (response.status === 200) {
        await fetchUserFinancialDetails(selectedUser._id)
        setDeleteDialogOpen(false);
        notify('Registro excluído');
      }
    } catch (error) {
      notify('Erro ao excluir registro!', 'error');
    }
  }

  // Gera (ou reaproveita) o link de pagamento PIX para uma cobrança e envia por e-mail.
  const handleCreatePaymentLink = async (recordId) => {
    try {
      const response = await api.post('/pagamentos/cobranca', { recordId });
      const link = `${window.location.origin}/pagamento/${response.data.data.link.split('/pagamento/')[1]}`;
      await navigator.clipboard.writeText(link).catch(() => {});
      notify('Link de pagamento enviado por e-mail e copiado.');
    } catch (error) {
      notify(error.response?.data?.error || 'Erro ao gerar link de pagamento', 'error');
    }
  };

  const fetchUserFinancialDetails = async (userId) => {
    try {
      const response = await api.post('/getRecordFinancialPlayer', { user_id: userId });
      if (response.data.data.saldo !== undefined && response.data.data.saldo !== null) {
        setBalance(response.data.data.saldo);
      }

      setFinancialData(response.data.data.sortedRecords.map((record, index) => ({
        id: index,
        ...record,
        amount: record.type === 'charge' ? -record.amount : record.amount,
      })));
    } catch (error) {
      console.error('Erro ao obter dados financeiros do usuário:', error);
    }
  };

  useEffect(() => {
    if (selectedUser) {
      fetchUserFinancialDetails(selectedUser._id);
    }
  }, [selectedUser]);

  // Análise do mês: peladas jogadas × cobranças (para conferir mensalidades e diárias)
  useEffect(() => {
    if (!selectedUser || !mes) return;
    setAnalise(null);
    api
      .get(`admin/jogadores/${selectedUser._id}/mes`, { params: { mes } })
      .then((r) => setAnalise(r.data))
      .catch(() => setAnalise({ erro: true }));
  }, [selectedUser, mes]);

  const doMes = (record) => mesDe(record.createdAt) === mes;
  const linhas = soDoMes ? financialData.filter(doMes) : financialData;

  const financialColumns = [
    { field: 'description', headerName: 'Descrição', flex: 1, minWidth: 220 },
    {
      field: 'amount', headerName: 'Valor', width: 130, type: 'number', renderCell: (params) => (
        <span style={{ color: params.row.amount < 0 ? '#C62828' : '#1F9D55', fontWeight: 600 }}>{formatCurrency(params.row.amount)}</span>
      ),
    },
    // Sempre no horário de Brasília, independente do fuso do aparelho de quem consulta
    { field: 'createdAt', headerName: 'Data e hora', width: 170, valueFormatter: (params) => (params.value ? new Date(params.value).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo' }) : '') },
    {
      field: 'acoes',
      headerName: 'Ações',
      width: 100,
      sortable: false,
      renderCell: (params) => (
        <>
          {params.row.type === 'charge' && (
            <Tooltip title="Enviar link de pagamento PIX">
              <IconButton size="small" color="primary" onClick={() => handleCreatePaymentLink(params.row._id)}>
                <PixRoundedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Excluir lançamento">
            <IconButton size="small" onClick={() => { setRegisterSelect(params.row._id); setDeleteDialogOpen(true); }}>
              <DeleteIcon fontSize="small" color="error" />
            </IconButton>
          </Tooltip>
        </>
      ),
    },
  ];

  return (
    <Stack spacing={2}>
      <Autocomplete
        options={users}
        getOptionLabel={(option) => option.nome}
        value={selectedUser}
        onChange={(event, newValue) => setSelectedUser(newValue)}
        renderOption={(props, option) => (
          <Box component="li" {...props} key={option._id} sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
            <PlayerAvatar src={option.foto} name={option.nome} size={28} />
            {option.nome}
          </Box>
        )}
        renderInput={(params) => <TextField {...params} label="Jogador" />}
        sx={{ maxWidth: 480 }}
      />
      {selectedUser ? (
        <>
          <Stack direction="row" spacing={2} alignItems="center" sx={{ p: 2, borderRadius: 2, bgcolor: balance < 0 ? 'error.main' : 'success.main', color: '#fff' }}>
            <PlayerAvatar src={selectedUser.foto} name={selectedUser.nome} size={44} />
            <Box>
              <Typography variant="body2" sx={{ opacity: 0.85 }}>Saldo de {selectedUser.nome}</Typography>
              <Typography sx={{ fontSize: '1.6rem', fontWeight: 700 }}>{formatCurrency(balance)}</Typography>
            </Box>
          </Stack>
          <Box sx={{ p: 2, borderRadius: 2, border: 1, borderColor: 'divider' }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }} sx={{ mb: 1.5 }}>
              <Typography fontWeight={700} sx={{ flexGrow: 1 }}>Análise do mês</Typography>
              <TextField type="month" size="small" label="Mês" value={mes} onChange={(e) => setMes(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ minWidth: 170 }} />
              <FormControlLabel control={<Switch checked={soDoMes} onChange={(e) => setSoDoMes(e.target.checked)} />} label="Extrato só deste mês" />
            </Stack>
            <AnaliseMes analise={analise} />
          </Box>
          <DataGrid
            rows={linhas}
            columns={financialColumns}
            autoHeight
            disableColumnMenu
            disableRowSelectionOnClick
            initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
            pageSizeOptions={[10, 25]}
          />
        </>
      ) : (
        <EmptyState title="Selecione um jogador" description="O extrato mostra cobranças (negativas) e pagamentos (positivos)." />
      )}

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Excluir lançamento</DialogTitle>
        <DialogContent>
          <DialogContentText>Tem certeza de que deseja excluir? Cobranças PIX pendentes deste lançamento serão canceladas.</DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteDialogOpen(false)} color="inherit">Cancelar</Button>
          <Button onClick={handleDeleteRegister} variant="contained" color="error">Excluir</Button>
        </DialogActions>
      </Dialog>
      <Snackbar open={openMessage} autoHideDuration={6000} onClose={() => setOpenMessage(false)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <MuiAlert onClose={() => setOpenMessage(false)} severity={severity} elevation={6} variant="filled">
          {message}
        </MuiAlert>
      </Snackbar>
    </Stack>
  );
};

export default FinancialUserDetails;
